import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import $ from 'jquery';
import Select from 'react-select';
import { Icon } from '@iconify/react';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import {
  createFormType,
  deleteFormType,
  getFormType,
  updateFormType,
} from '../api/form-typeAPI';
import { mageIconOptions } from '../constants/mageIconOptions';

const FormTypeListLayer = () => {
  const userProfile = useUserProfile();
  const [formTypes, setFormTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formType, setFormType] = useState({ name: '', icon: '' });
  const [errors, setErrors] = useState({});
  const tableRef = useRef(null);
  const dtRef = useRef(null);

  const hasPrivilege = (priv) => userProfile?.privileges?.includes(priv);
  const profileLoaded = userProfile !== null && userProfile !== undefined;
  const canView = !!hasPrivilege('form-type:view');

  const sortedFormTypes = useMemo(
    () =>
      [...formTypes].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      ),
    [formTypes]
  );

  const formatIconLabel = useCallback((label) => {
    if (!label) return '';
    return label
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }, []);

  const iconSelectOptions = useMemo(
    () => [
      { value: '', label: 'No icon' },
      ...mageIconOptions.map((option) => ({
        value: option.value,
        label: formatIconLabel(option.label),
      })),
    ],
    [formatIconLabel]
  );

  const formatSelectOptionLabel = useCallback((option) => (
    <div className='d-flex align-items-center gap-2'>
      {option.value ? <Icon icon={option.value} className='text-lg' /> : <span className='text-muted'>—</span>}
      <span>{option.label}</span>
    </div>
  ), []);

  const fetchFormTypes = useCallback(async () => {
    if (!canView) return;
    try {
      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }

      const data = await getFormType();
      setFormTypes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching form types:', error);
    }
  }, [canView]);

  useEffect(() => {
    if (!profileLoaded) return;
    if (!canView) {
      setFormTypes([]);
      return;
    }
    fetchFormTypes();
  }, [canView, profileLoaded, fetchFormTypes]);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.destroy();
      dtRef.current = null;
    }
    if (sortedFormTypes.length > 0) {
      dtRef.current = $(tableRef.current).DataTable({
        pageLength: 25,
        dom: 'rtp',
        responsive: false,
        language: {
          paginate: { first: '', last: '', next: '', previous: '' },
        },
      });
    }
    return () => {
      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }
    };
  }, [sortedFormTypes]);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.search(searchTerm).draw();
    }
  }, [searchTerm]);

  const validate = () => {
    const nextErrors = {};
    if (!formType.name?.trim()) {
      nextErrors.name = 'Name is required';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const openModal = (item = { name: '', icon: '' }) => {
    setFormType({ name: item.name ?? '', icon: item.icon ?? '' });
    setEditingId(item.id ?? null);
    setErrors({});
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormType({ name: '', icon: '' });
    setEditingId(null);
    setErrors({});
  };

  const handleSave = async () => {
    if (!validate()) return;
    try {
      const payload = {
        name: formType.name.trim(),
        icon: formType.icon?.trim() || '',
      };

      if (editingId) {
        await updateFormType(editingId, payload);
      } else {
        await createFormType(payload);
      }

      closeModal();
      await fetchFormTypes();
    } catch (error) {
      console.error('Error saving form type:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this form type?')) return;
    try {
      await deleteFormType(id);
      await fetchFormTypes();
    } catch (error) {
      console.error('Error deleting form type:', error);
    }
  };

  return (
    <div className='card h-100 p-0 rounded-0'>
      <div className='card-header d-flex justify-content-between align-items-center p-3'>
        <input
          type='text'
          className='form-control w-auto'
          placeholder='Search...'
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          disabled={!canView}
        />
        {hasPrivilege('form-type:create') && (
          <button
            className='btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2'
            onClick={() => openModal()}
          >
            <Icon icon='ic:baseline-plus' className='icon text-xl' />
            Add Form Type
          </button>
        )}
      </div>

      <div className='card-body p-0'>
        {profileLoaded && !canView ? (
          <div className='p-4'>
            <p className='mb-0 text-muted'>You do not have permission to view form types.</p>
          </div>
        ) : (
          <div className='table-responsive scroll-sm'>
            <table className='table bordered-table mb-0 w-100 rounded-0' ref={tableRef}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Icon</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedFormTypes.map((item) => (
                  <tr key={item.id}>
                    <td><strong>{item.name}</strong></td>
                    <td>
                      {item.icon ? (
                        <div className='d-flex flex-column align-items-start gap-1'>
                          <div className='d-flex align-items-center gap-2'>
                            <Icon icon={item.icon} className='text-xl' />
                          </div>
                        </div>
                      ) : (
                        <span className='text-muted'>No icon</span>
                      )}
                    </td>
                    <td>
                      <div className='d-flex gap-2'>
                        {hasPrivilege('form-type:update') && (
                          <button
                            className='w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center'
                            onClick={() => openModal(item)}
                          >
                            <Icon icon='ic:baseline-edit' className='icon text-xl line-height-1' />
                          </button>
                        )}
                        {hasPrivilege('form-type:delete') && (
                          <button
                            className='w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center'
                            onClick={() => handleDelete(item.id)}
                          >
                            <Icon icon='ic:baseline-delete-outline' className='icon text-xl line-height-1' />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {sortedFormTypes.length === 0 && (
                  <tr>
                    <td colSpan={3} className='text-center py-2 text-muted'>
                      No form types found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className='modal show d-block' tabIndex='-1'>
          <div className='modal-dialog modal-md'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>{editingId ? 'Edit Form Type' : 'New Form Type'}</h5>
                <button type='button' className='btn-close' onClick={closeModal} />
              </div>
              <div className='modal-body'>
                <div className='mb-3'>
                  <label className='form-label'>Name</label>
                  <input
                    type='text'
                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                    value={formType.name}
                    onChange={(e) => setFormType((prev) => ({ ...prev, name: e.target.value }))}
                  />
                  {errors.name && <div className='invalid-feedback'>{errors.name}</div>}
                </div>
                <div className='mb-0'>
                  <label className='form-label mb-2'>Icon</label>
                  <div className='form-text mb-12'>Select an optional icon to represent this form type.</div>
                  <Select
                    className='react-select-container'
                    classNamePrefix='react-select'
                    value={
                      iconSelectOptions.find((option) => option.value === formType.icon) ??
                      iconSelectOptions[0]
                    }
                    onChange={(selectedOption) =>
                      setFormType((prev) => ({
                        ...prev,
                        icon: selectedOption?.value ?? '',
                      }))
                    }
                    options={iconSelectOptions}
                    formatOptionLabel={formatSelectOptionLabel}
                    isSearchable
                  />
                </div>
              </div>
              <div className='modal-footer'>
                <button className='btn btn-secondary' onClick={closeModal}>
                  Cancel
                </button>
                <button className='btn btn-primary' onClick={handleSave}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormTypeListLayer;
